import { useMemo, useState } from 'react';
import type { PortalUser } from '@urbanrats/ops-core';
import type { LoginAuth } from '../backend/types.ts';
import { encodeQr, qrSvgPath } from '../lib/qr.ts';

type Step =
  | { kind: 'credentials' }
  | { kind: 'enroll'; challenge: string; secret: string; uri: string }
  | { kind: 'code'; challenge: string }
  | { kind: 'password'; user: PortalUser; weak: boolean };

/**
 * Sign-in: user id + password, then the authenticator code. A new account enrols its authenticator app here
 * (QR code, or the key typed by hand) and only a correct code from the app completes enrolment. After an
 * admin-issued or weak password the user is offered a password change (may skip).
 */
export function SignIn({ auth, onDone, notice = null }: { auth: LoginAuth; onDone: (u: PortalUser) => void; notice?: string | null }) {
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setMsg(null);
    try {
      await f();
    } finally {
      setBusy(false);
    }
  };
  const submitCode = (challenge: string) =>
    run(async () => {
      const r = await auth.code(challenge, code.trim());
      setCode('');
      if (!r.ok) {
        setMsg(r.error);
        if (/expired/.test(r.error)) setStep({ kind: 'credentials' });
        return;
      }
      if (r.value.suggestPasswordChange) setStep({ kind: 'password', user: r.value.user, weak: r.value.passwordWeak });
      else {
        setPassword('');
        onDone(r.value.user);
      }
    });

  return (
    <main className="auth-page" data-testid="login">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="mark">◆</span>
          <span>
            UrbanRats <small>operations</small>
          </span>
        </div>
        {notice && (
          <p className="warn-box" role="status" data-testid="session-ended">
            {notice}
          </p>
        )}
        {step.kind === 'credentials' && (
          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                const r = await auth.password(userId.trim(), password);
                if (!r.ok) return setMsg(r.error);
                if (r.value.stage === 'ENROLL' && r.value.enrolment) setStep({ kind: 'enroll', challenge: r.value.challenge, ...r.value.enrolment });
                else setStep({ kind: 'code', challenge: r.value.challenge });
              });
            }}
          >
            <h1>Sign in</h1>
            <p className="muted">Password, then the 6-digit code from your authenticator app.</p>
            <label htmlFor="login-user">User</label>
            <input id="login-user" value={userId} onChange={(e) => setUserId(e.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} data-testid="login-user" required />
            <label htmlFor="login-password">Password</label>
            <input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password" />
            <button type="submit" className="btn primary" disabled={busy || !userId.trim()} data-testid="login-submit">
              Continue
            </button>
          </form>
        )}
        {step.kind === 'enroll' && (
          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode(step.challenge);
            }}
          >
            <h1>Set up your authenticator</h1>
            <p className="muted">First sign-in: scan this code with Google Authenticator (or any TOTP app), then enter the 6-digit code it shows.</p>
            <Qr text={step.uri} />
            <details className="auth-secret">
              <summary>Can’t scan? Enter the key by hand</summary>
              <code data-testid="enroll-secret">{step.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              <span className="muted">Account: UrbanRats Ops · time-based · 6 digits · 30 s</span>
            </details>
            <label htmlFor="login-code">Code from the app</label>
            <input id="login-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7} data-testid="login-code" autoFocus />
            <button type="submit" className="btn primary" disabled={busy || code.replace(/\s/g, '').length !== 6} data-testid="login-submit">
              Verify and sign in
            </button>
            <p className="note">The key is shown only during this sign-in. Lost the app later? An admin resets your account.</p>
          </form>
        )}
        {step.kind === 'code' && (
          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode(step.challenge);
            }}
          >
            <h1>Authenticator code</h1>
            <p className="muted">Enter the 6-digit code from your authenticator app for {userId.trim()}.</p>
            <label htmlFor="login-code">Code</label>
            <input id="login-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7} data-testid="login-code" autoFocus />
            <button type="submit" className="btn primary" disabled={busy || code.replace(/\s/g, '').length !== 6} data-testid="login-submit">
              Sign in
            </button>
            <button type="button" className="btn ghost" onClick={() => (setStep({ kind: 'credentials' }), setCode(''))}>
              Back
            </button>
          </form>
        )}
        {step.kind === 'password' && (
          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (next !== again) return setMsg('The two new passwords differ');
              void run(async () => {
                const r = await auth.changePassword(password, next);
                if (!r.ok) return setMsg(r.error);
                setPassword('');
                onDone(step.user);
              });
            }}
          >
            <h1>Choose your own password</h1>
            <p className="muted">
              {step.weak ? 'Your current password is weak. ' : 'You signed in with a password an admin issued. '}
              We recommend a new one now: at least 10 characters, three of lower case, upper case, digits and symbols.
            </p>
            <label htmlFor="pw-new">New password</label>
            <input id="pw-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="pw-new" />
            <PasswordMeter pw={next} />
            <label htmlFor="pw-again">New password again</label>
            <input id="pw-again" type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" data-testid="pw-again" />
            <button type="submit" className="btn primary" disabled={busy || next.length < 10} data-testid="pw-save">
              Save and continue
            </button>
            <button
              type="button"
              className="btn ghost"
              data-testid="pw-skip"
              onClick={() => {
                setPassword('');
                onDone(step.user);
              }}
            >
              Not now
            </button>
          </form>
        )}
        {msg && (
          <p role="alert" className="bad-text">
            {msg}
          </p>
        )}
      </div>
    </main>
  );
}

function Qr({ text }: { text: string }) {
  const { path, size } = useMemo(() => qrSvgPath(encodeQr(text)), [text]);
  return (
    <svg className="auth-qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Authenticator enrolment QR code" data-testid="enroll-qr" shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

export function PasswordMeter({ pw }: { pw: string }) {
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  const score = pw.length === 0 ? 0 : pw.length < 10 || classes < 3 ? 1 : pw.length < 14 ? 2 : 3;
  const label = ['', 'too weak', 'good', 'strong'][score];
  return (
    <div className={`pw-meter s${score}`} aria-live="polite">
      <span />
      <span />
      <span />
      <em>{label}</em>
    </div>
  );
}
